define((require) => {
    "use strict";

    const Backbone  = require("backbone");

    /**
     * A virtual Backbone collection containing the concatenation of multiple models and/or collections.
     *
     * <p>This collection is only suitable for use as a view of the underlying models/collections. Avoid calling
     * functions that manipulate the collection, as this has undefined behaviour.</p>
     *
     * <p>When a sub-collection is reset or sorted, this collection will fire a reset event. Adds and removes are
     * correctly propagated through.</p>
     *
     * <p>Note that because this collection is modelled as a Backbone collection, it is not valid to have the same
     * model appear multiple times in the collection. If you concatenate two collections that each contain the same
     * model, the behaviour is undefined.</p>
     *
     * @author Bruce Mackenzie Nielsen
     * @class ConcatenatedCollection
     */
    return Backbone.Collection.extend({

        _contents: null,

        _getModelArray() {
            const models = [];

            this._contents.forEach((modelOrCollection) => {
                if (modelOrCollection instanceof Backbone.Collection) {
                    models.push.apply(models, modelOrCollection.models);
                } else {
                    models.push(modelOrCollection);
                }
            });

            return models;
        },

        /**
         * Gets the start index in the concatenated collection of the given subcollection.
         *
         * <p>If the subcollection is not part of the concatenated collection, returns -1.</p>
         *
         * @param {Backbone.Collection} collection The collection.
         * @returns {number} The index, or -1.
         * @private
         */
        _getCollectionStartIndex(collection) {
            let index = 0;

            const modelOrCollection = this._contents.find((modelOrCollection) => {
                // Break out of the loop if this is the requested collection
                if (modelOrCollection === collection) {
                    return true;
                }
                // Increment the index by the appropriate amount
                index += (modelOrCollection instanceof Backbone.Collection) ? modelOrCollection.length : 1;

                return false;
            });

            if (!modelOrCollection) {
                return -1;
            }

            return index;
        },

        _subCollectionResetHandler() {
            this.reset(this._getModelArray());
        },

        _subCollectionAddHandler(model, collection, options) {
            const offset = this._getCollectionStartIndex(collection);
            const index = (options.at === undefined || options.at === null) ? (collection.length - 1) : options.at; // options.at might be 0, so can't just do options.at || collection.length - 1
            // Guard for someone changing the collection manually
            if (offset > -1) {
                this.add(model, {at: offset + index});
            }
        },

        _subCollectionRemoveHandler(model) {
            this.remove(model);
        },

        _setupContentsArray(contents) {
            // Store references to the contents
            this._contents = contents;

            // Register event handlers
            contents.forEach((modelOrCollection) => {
                // Only need to register event handlers for collections
                if (modelOrCollection instanceof Backbone.Collection) {
                    this.listenTo(modelOrCollection, "reset sort", this._subCollectionResetHandler);
                    this.listenTo(modelOrCollection, "add", this._subCollectionAddHandler);
                    this.listenTo(modelOrCollection, "remove", this._subCollectionRemoveHandler);
                }
            });
        },

        constructor(...args) {
            this._setupContentsArray(args);
            this.__super(this._getModelArray());
        },

        /**
         * Sets the contents of the concatenated collection. Fires a reset on the collection with the new contents.
         */
        setContents(...args) {
            // Unregister existing event handlers
            this.stopListening();

            // Set up the contents and reset
            this._setupContentsArray(args);
            this.reset(this._getModelArray());
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/zbone/ConcatenatedCollection.js